import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { UploaderService } from '../services/uploaderService';
import type { Event } from '@/types/schema';
import {
    UploadBatch,
    PhotographerInfo,
    UploadResult,
    PhotoMetadata
} from '../models/uploader.types';

// Hook para buscar informações do evento
export const useEventInfo = (eventId: string) => {
    return useQuery({
        queryKey: ['eventInfo', eventId],
        queryFn: () => UploaderService.getEventInfo(eventId),
        enabled: !!eventId
    });
};

// Hook para buscar eventos do fotógrafo
export const usePhotographerEvents = (photographerId: string) => {
    return useQuery({
        queryKey: ['photographerEvents', photographerId],
        queryFn: () => UploaderService.getEventsByPhotographer(photographerId),
        enabled: !!photographerId
    });
};

// Hook para buscar informações do batch
export const useBatch = (batchId: string) => {
    return useQuery({
        queryKey: ['batch', batchId],
        queryFn: () => UploaderService.getBatch(batchId),
        enabled: !!batchId
    });
};

// Hook para criar batch
export const useCreateBatch = () => {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationFn: ({ eventId, photographerId }: { eventId: string; photographerId: string }) =>
            UploaderService.createBatch(eventId, photographerId),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['photographerEvents'] });
            queryClient.invalidateQueries({ queryKey: ['batch'] });
            toast({
                title: 'Batch criado com sucesso!',
                description: `Batch ${data.id} foi criado para o evento.`
            });
        },
        onError: (error: any) => {
            toast({
                title: 'Erro ao criar batch',
                description: error.message || 'Erro desconhecido',
                variant: 'destructive'
            });
        }
    });
};

// Hook para atualizar status do batch
export const useUpdateBatchStatus = () => {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationFn: ({ batchId, status }: { batchId: string; status: string }) =>
            UploaderService.updateBatchStatus(batchId, status),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['batch', data.id] });
            queryClient.invalidateQueries({ queryKey: ['photographerEvents'] });
            toast({
                title: 'Status atualizado!',
                description: `Batch ${data.id} atualizado para ${data.status}.`
            });
        },
        onError: (error: any) => {
            toast({
                title: 'Erro ao atualizar status',
                description: error.message || 'Erro desconhecido',
                variant: 'destructive'
            });
        }
    });
};

// Hook para upload de fotos
export const useUploadPhotos = () => {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationFn: ({
            batchId,
            photos,
            onProgress
        }: {
            batchId: string;
            photos: { file: File; metadata: PhotoMetadata }[];
            onProgress?: (progress: any) => void;
        }) =>
            UploaderService.uploadPhotos(batchId, photos, onProgress),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['batch'] });
            queryClient.invalidateQueries({ queryKey: ['photographerEvents'] });
            toast({
                title: 'Upload concluído!',
                description: `${data.uploadedPhotos || 0} fotos enviadas com sucesso.`
            });
        },
        onError: (error: any) => {
            toast({
                title: 'Erro no upload',
                description: error.message || 'Erro desconhecido',
                variant: 'destructive'
            });
        }
    });
};

// Hook para upload de foto única
export const useUploadSinglePhoto = () => {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationFn: ({
            batchId,
            file,
            metadata,
            onProgress
        }: {
            batchId: string;
            file: File;
            metadata: PhotoMetadata;
            onProgress?: (progress: number) => void;
        }) =>
            UploaderService.uploadSinglePhoto(batchId, file, metadata, onProgress),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['batch'] });
            toast({
                title: 'Foto enviada!',
                description: 'Foto enviada com sucesso.'
            });
        },
        onError: (error: any) => {
            toast({
                title: 'Erro no upload',
                description: error.message || 'Erro desconhecido',
                variant: 'destructive'
            });
        }
    });
};

// Hook para análise de foto
export const useAnalyzePhoto = () => {
    const { toast } = useToast();

    return useMutation({
        mutationFn: (file: File) => UploaderService.analyzePhoto(file),
        onError: (error: any) => {
            toast({
                title: 'Erro na análise',
                description: error.message || 'Erro desconhecido',
                variant: 'destructive'
            });
        }
    });
};

// Hook para estatísticas do fotógrafo
export const usePhotographerStats = (photographerId: string) => {
    return useQuery({
        queryKey: ['photographerStats', photographerId],
        queryFn: () => UploaderService.getPhotographerStats(photographerId),
        enabled: !!photographerId
    });
};

// Hook para estatísticas do evento
export const useEventStats = (eventId: string) => {
    return useQuery({
        queryKey: ['eventStats', eventId],
        queryFn: () => UploaderService.getEventStats(eventId),
        enabled: !!eventId
    });
};

// Hook para histórico de uploads
export const useUploadHistory = (photographerId: string, limit = 10) => {
    return useQuery({
        queryKey: ['uploadHistory', photographerId, limit],
        queryFn: () => UploaderService.getUploadHistory(photographerId, limit),
        enabled: !!photographerId
    });
};

// Hook para limpeza de uploads falhados
export const useCleanupFailedUploads = () => {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationFn: (batchId: string) => UploaderService.cleanupFailedUploads(batchId),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['batch'] });
            queryClient.invalidateQueries({ queryKey: ['uploadHistory'] });
            toast({
                title: 'Limpeza concluída!',
                description: data.message
            });
        },
        onError: (error: any) => {
            toast({
                title: 'Erro na limpeza',
                description: error.message || 'Erro desconhecido',
                variant: 'destructive'
            });
        }
    });
};
